import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { LucideIconsModule } from '../../lucide-icons.module';
import { stratusProdFallbackUrl } from '../../utils/stratus-url.util';

export type LuxuryViewerImage = {
    url?: string | null;
    thumb?: string | null;
    alt?: string | null;
    isMain?: boolean | null;
};

@Component({
    selector: 'app-luxury-image-viewer',
    standalone: true,
    imports: [CommonModule, LucideIconsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './luxury-image-viewer.component.html',
    styleUrls: ['./luxury-image-viewer.component.scss'],
})
export class LuxuryImageViewerComponent implements OnInit, OnChanges, OnDestroy {
    private readonly doc = inject(DOCUMENT);
    private readonly destroyRef = inject(DestroyRef);

    @Input() images: LuxuryViewerImage[] = [];
    @Input() startIndex = 0;
    @Input() title: string | null = null;

    @Output() closed = new EventEmitter<void>();

    safeIndex = 0;
    currentUrl: string | null = null;
    currentAlt: string | null = null;
    loading = false;
    upgrading = false;

    maxZoom = 4;
    zoom = 1;
    translateX = 0;
    translateY = 0;
    dragging = false;

    private readonly failedUrls = new Set<string>();
    private readonly failedThumbs = new WeakSet<LuxuryViewerImage>();
    private loadSeq = 0;

    private previousBodyOverflow: string | null = null;
    private pointerStart: { x: number; y: number; t: number } | null = null;
    private dragStart: { x: number; y: number; translateX: number; translateY: number } | null = null;

    get imageTransform(): string {
        return `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.zoom})`;
    }

  ngOnInit(): void {
    this.lockScroll();
    this.resetTo(this.startIndex);
    this.destroyRef.onDestroy(() => this.unlockScroll());
  }

    ngOnChanges(changes: SimpleChanges): void {
        const imagesChanged = Boolean(changes['images']);
        const indexChanged = Boolean(changes['startIndex']);
        if (!imagesChanged && !indexChanged) return;
        this.resetTo(this.startIndex);
    }

    ngOnDestroy(): void {
        this.unlockScroll();
    }

    @HostListener('document:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        const key = event.key;
        if (key === 'Escape') {
            this.close();
            event.preventDefault();
            return;
        }
        if (key === 'ArrowLeft') {
            this.prev();
            event.preventDefault();
            return;
        }
        if (key === 'ArrowRight') {
            this.next();
            event.preventDefault();
        }
    }

    close(event?: Event): void {
        event?.stopPropagation();
        this.closed.emit();
    }

    prev(): void {
        if (this.images.length < 2) return;
        this.goTo((this.safeIndex - 1 + this.images.length) % this.images.length);
    }

    next(): void {
        if (this.images.length < 2) return;
        this.goTo((this.safeIndex + 1) % this.images.length);
    }

    goTo(index: number): void {
        if (!this.images.length) return;
        const next = Math.min(Math.max(0, index), this.images.length - 1);
        this.safeIndex = next;
        this.resetZoom();
        void this.loadCurrent();
    }

    resolveThumb(img: LuxuryViewerImage): string | null {
        if (!img || this.failedThumbs.has(img)) return this.resolveUrl(img);
        const thumb = String(img.thumb || '').trim();
        if (thumb) return thumb;
        return this.resolveUrl(img);
    }

    markThumbError(img: LuxuryViewerImage): void {
        if (!img) return;
        this.failedThumbs.add(img);
    }

    onPointerDown(event: PointerEvent): void {
        if (event.button !== 0) return;
        event.preventDefault();
        try {
            (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
        } catch {
            // ignore
        }
        this.pointerStart = { x: event.clientX, y: event.clientY, t: Date.now() };
        if (this.zoom > 1) {
            this.dragging = true;
            this.dragStart = { x: event.clientX, y: event.clientY, translateX: this.translateX, translateY: this.translateY };
        }
    }

    onPointerMove(event: PointerEvent): void {
        if (!this.dragging || !this.dragStart) return;
        event.preventDefault();
        const dx = event.clientX - this.dragStart.x;
        const dy = event.clientY - this.dragStart.y;
        const nextX = this.dragStart.translateX + dx;
        const nextY = this.dragStart.translateY + dy;
        const maxPan = Math.max(0, (this.zoom - 1) * 320);
        this.translateX = Math.min(maxPan, Math.max(-maxPan, nextX));
        this.translateY = Math.min(maxPan, Math.max(-maxPan, nextY));
    }

    onPointerUp(event: PointerEvent): void {
        const start = this.pointerStart;
        this.pointerStart = null;

        if (this.dragging) {
            event.preventDefault();
            this.dragging = false;
            this.dragStart = null;
            return;
        }
        if (!start) return;

        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        const dt = Date.now() - start.t;

        if (dt > 550) return;
        if (Math.abs(dx) < 60) return;
        if (Math.abs(dx) < Math.abs(dy)) return;

        if (dx > 0) this.prev();
        else this.next();
    }

    onPointerCancel(): void {
        this.pointerStart = null;
        this.dragging = false;
        this.dragStart = null;
    }

    zoomIn(): void {
        this.setZoom(this.zoom * 1.2);
    }

    zoomOut(): void {
        this.setZoom(this.zoom / 1.2);
    }

    toggleZoom(event?: Event): void {
        event?.preventDefault();
        if (this.zoom === 1) this.setZoom(2);
        else this.resetZoom();
    }

    resetZoom(): void {
        this.zoom = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.dragging = false;
        this.dragStart = null;
    }

    onWheel(event: WheelEvent): void {
        event.preventDefault();
        const direction = event.deltaY > 0 ? -1 : 1;
        const factor = direction > 0 ? 1.1 : 0.9;
        this.setZoom(this.zoom * factor);
    }

    private setZoom(next: number): void {
        const clamped = Math.min(this.maxZoom, Math.max(1, next));
        this.zoom = clamped;
        if (clamped === 1) {
            this.translateX = 0;
            this.translateY = 0;
        } else {
            const maxPan = Math.max(0, (this.zoom - 1) * 320);
            this.translateX = Math.min(maxPan, Math.max(-maxPan, this.translateX));
            this.translateY = Math.min(maxPan, Math.max(-maxPan, this.translateY));
        }
    }

    onImageLoaded(event?: Event): void {
        const target = event?.target as HTMLImageElement | null;
        const loadedUrl = target?.currentSrc || target?.src || '';
        if (this.currentUrl && loadedUrl && loadedUrl !== this.currentUrl) return;
        this.loading = false;
    }

    onImageFailed(_event?: Event): void {
        const failedUrl = this.currentUrl;
        if (failedUrl) this.failedUrls.add(failedUrl);

        const hostFallback = failedUrl ? stratusProdFallbackUrl(failedUrl) : null;
        if (hostFallback && !this.failedUrls.has(hostFallback)) {
            this.loading = true;
            this.upgrading = false;
            this.currentUrl = hostFallback;
            return;
        }

        const img = this.images[this.safeIndex];
        const rawThumb = this.rawThumb(img);
        if (failedUrl && rawThumb && failedUrl === rawThumb) this.failedThumbs.add(img);

        const fallback = this.pickFallbackUrl(img, failedUrl);
        if (fallback) {
            this.loading = true;
            this.upgrading = false;
            this.currentUrl = fallback;
            return;
        }

        this.currentUrl = null;
        this.currentAlt = null;
        this.loading = false;
        this.upgrading = false;
    }

    private resetTo(index: number): void {
        if (!Array.isArray(this.images)) this.images = [];
        if (!this.images.length) {
            this.safeIndex = 0;
            this.currentUrl = null;
            this.currentAlt = null;
            this.loading = false;
            this.resetZoom();
            return;
        }
        const safe = Math.min(Math.max(0, Number(index) || 0), this.images.length - 1);
        this.safeIndex = safe;
        this.resetZoom();
        void this.loadCurrent({ eager: true });
    }

    private resolveUrl(img: LuxuryViewerImage): string | null {
        const url = String(img?.url || '').trim();
        if (url) return url;
        const thumb = String(img?.thumb || '').trim();
        return thumb ? thumb : null;
    }

    private async loadCurrent(options: { eager?: boolean } = {}): Promise<void> {
        const img = this.images[this.safeIndex];
        this.currentAlt = img?.alt ? String(img.alt) : null;

        const fullUrl = this.resolveUrl(img);
        const thumbUrl = this.resolveThumb(img);
        const initialUrl = this.pickInitialUrl(thumbUrl, fullUrl);

        if (!initialUrl) {
            this.currentUrl = null;
            this.loading = false;
            this.upgrading = false;
            return;
        }

        const seq = ++this.loadSeq;

        if (initialUrl !== this.currentUrl) {
            this.loading = true;
            this.currentUrl = initialUrl;
        } else if (!this.currentUrl) {
            this.loading = true;
            this.currentUrl = initialUrl;
        }

        const shouldUpgrade = Boolean(fullUrl && fullUrl !== initialUrl && !this.failedUrls.has(fullUrl));
        this.upgrading = shouldUpgrade;

        if (shouldUpgrade && fullUrl) {
            void this.preloadImage(fullUrl).then((loaded) => {
                if (seq !== this.loadSeq) return;
                this.upgrading = false;
                if (!loaded) return;
                if (this.currentUrl !== initialUrl) return;
                this.currentUrl = fullUrl;
            });
        }

        if (options.eager) {
            Promise.resolve().then(() => undefined);
        }

        this.preloadNeighbors();
    }

    private preloadNeighbors(): void {
        if (this.images.length < 2) return;
        const candidates = [
            (this.safeIndex - 1 + this.images.length) % this.images.length,
            (this.safeIndex + 1) % this.images.length
        ];
        for (const idx of candidates) {
            const thumb = this.rawThumb(this.images[idx]);
            if (!thumb || this.failedUrls.has(thumb)) continue;
            void this.preloadImage(thumb);
        }
    }

    private pickInitialUrl(thumbUrl: string | null, fullUrl: string | null): string | null {
        const thumb = thumbUrl && !this.failedUrls.has(thumbUrl) ? thumbUrl : null;
        const full = fullUrl && !this.failedUrls.has(fullUrl) ? fullUrl : null;
        return thumb || full || null;
    }

    private pickFallbackUrl(img: LuxuryViewerImage | undefined, failedUrl: string | null): string | null {
        if (!img) return null;
        const candidates = [this.resolveUrl(img), this.resolveThumb(img)];
        const expanded: string[] = [];
        const seen = new Set<string>();
        for (const candidate of candidates) {
            const url = String(candidate || '').trim();
            if (!url) continue;
            if (!seen.has(url)) {
                seen.add(url);
                expanded.push(url);
            }
            const fallback = stratusProdFallbackUrl(url);
            if (fallback && !seen.has(fallback)) {
                seen.add(fallback);
                expanded.push(fallback);
            }
        }

        for (const url of expanded) {
            if (failedUrl && url === failedUrl) continue;
            if (this.failedUrls.has(url)) continue;
            return url;
        }
        return null;
    }

    private rawThumb(img: LuxuryViewerImage | undefined): string | null {
        const raw = String(img?.thumb || '').trim();
        return raw ? raw : null;
    }

    private preloadImage(url: string): Promise<boolean> {
        const target = String(url || '').trim();
        if (!target) return Promise.resolve(false);
        if (this.failedUrls.has(target)) return Promise.resolve(false);
        return new Promise<boolean>((resolve) => {
            const preload = new Image();
            preload.decoding = 'async';
            preload.onload = () => resolve(true);
            preload.onerror = () => resolve(false);
            preload.src = target;
        });
    }

    private lockScroll(): void {
        if (!this.doc?.body) return;
        if (this.previousBodyOverflow !== null) return;
        this.previousBodyOverflow = this.doc.body.style.overflow || '';
        this.doc.body.style.overflow = 'hidden';
    }

    private unlockScroll(): void {
        if (!this.doc?.body) return;
        if (this.previousBodyOverflow === null) return;
        this.doc.body.style.overflow = this.previousBodyOverflow;
        this.previousBodyOverflow = null;
    }
}
